import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt'
import PeopleIcon from '@mui/icons-material/PeopleOutline'
import UploadIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import ContactPhoneIcon from '@mui/icons-material/ContactPhoneOutlined'
import { format, money, type Customer, type CustomerQuery, type CurrencyCode } from '@iobiz/shared'
import { api } from '../api/client.js'
import { runCycle } from '../data/sync/engine.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import { useCities, useCustomers, useEtiquettes } from '../data/hooks.js'
import type { RequeteClients } from '../data/repositories/customers.local.js'
import { CustomerFormDialog } from '../features/customers/CustomerFormDialog.js'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { removeCustomer } from '../data/repositories/customers.write.js'
import { ImportDialog } from '../features/customers/ImportDialog.js'
import { ContactsTelephoneDialog } from '../features/customers/ContactsTelephoneDialog.js'
import { useSessionStore } from '../stores/sessionStore.js'

/** Liste des clients (W-08). */
export function CustomerListPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  // ⭐ Les filtres vivent dans l'URL (§7.9) : la vue est partageable,
  // rechargeable et navigable au bouton Précédent.
  const [params, setParams] = useSearchParams()
  const query: RequeteClients = useMemo(
    () => ({
      q: params.get('q') ?? undefined,
      city: params.get('city') ?? undefined,
      type: (params.get('type') as CustomerQuery['type']) ?? undefined,
      tag: params.get('tag') ?? undefined,
      sort: params.get('sort') ?? 'displayName',
      limit: Number(params.get('limit') ?? 50),
      offset: Number(params.get('offset') ?? 0),
    }),
    [params],
  )

  // ⭐ Les données viennent de la RÉPLIQUE LOCALE, pas du réseau. La liste
  // s'affiche instantanément et reste consultable hors ligne ; le moteur de
  // synchronisation la tient à jour en arrière-plan (§7.4).
  const page = useCustomers(store?.storeId, query)
  /**
   * ⭐ TOUTES les fiches du filtre courant, pas seulement la page affichée.
   * Verser « les clients » en n'envoyant que les cinquante lignes visibles
   * aurait été le genre d'export qu'on ne découvre incomplet qu'une fois le
   * répertoire rempli.
   *
   * ⚠️ Lue depuis la réplique locale, comme la liste : l'export fonctionne donc
   * au comptoir sans réseau, et le fichier de la clientèle ne fait pas un
   * aller-retour par le serveur pour revenir identique.
   */
  const toutes = useCustomers(store?.storeId, { ...query, limit: 5000, offset: 0 })
  const cities = useCities(store?.storeId)
  const etiquettes = useEtiquettes(store?.storeId)
  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const loading = page === undefined
  // Champ de saisie découplé de l'URL : on n'écrit l'URL qu'après l'anti-rebond.
  const [draft, setDraft] = useState(query.q ?? '')
  /** Échec à expliquer — un export refusé ne doit pas passer inaperçu. */
  const [message, setMessage] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [contactsOpen, setContactsOpen] = useState(false)
  const [selection, setSelection] = useState<string[]>([])
  const [confirmation, setConfirmation] = useState(false)

  // La palette de commandes propose « Créer un client « X » » et arrive ici
  // avec ?nouveau=X : le nom déjà tapé ne doit pas être ressaisi.
  const prefillName = params.get('nouveau') ?? undefined
  useEffect(() => {
    if (prefillName) setFormOpen(true)
  }, [prefillName])

  const patch = useCallback(
    (changes: Record<string, string | undefined>) => {
      const next = new URLSearchParams(params)
      for (const [key, value] of Object.entries(changes)) {
        if (value === undefined || value === '') next.delete(key)
        else next.set(key, value)
      }
      // Tout changement de filtre ramène à la première page : rester en page 4
      // d'un résultat qui n'en compte plus qu'une afficherait un vide trompeur.
      if (!('offset' in changes)) next.delete('offset')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  // Anti-rebond de 300 ms : une frappe ne déclenche pas une requête.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (draft !== (query.q ?? '')) patch({ q: draft })
    }, 300)
    return () => clearTimeout(timer)
  }, [draft, query.q, patch])

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      label: 'Nom',
      sortKey: 'displayName',
      render: (c) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar src={c.avatarUrl ?? undefined} sx={{ width: 28, height: 28, fontSize: 12 }}>
            {c.displayName.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {c.displayName}
          </Typography>
          {c.type === 'company' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Société
            </Typography>
          )}
          </Box>
        </Box>
      ),
    },
    {
      /**
       * La référence, juste après le nom : c'est elle qu'on épelle au
       * téléphone et qu'on lit sur la carte de fidélité, où elle figure sous
       * le code QR. Sans elle dans la liste, retrouver « le client CL-2026-0042 »
       * demandait d'ouvrir les fiches une à une.
       */
      key: 'code',
      label: 'Référence',
      sortKey: 'code',
      render: (c) =>
        c.code ? (
          <Typography variant="body2" className="tabular">
            {c.code}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
    {
      /**
       * ⚠️ Le COURRIEL n'est plus affiché ici. Il l'a été un temps, sous le
       * numéro : deux lignes par client pour une donnée qu'on ne lit pas au
       * comptoir, et qui allongeait la colonne de toute la longueur d'une
       * adresse. On appelle un client, on ne lui écrit pas depuis la liste.
       *
       * Il reste sur la FICHE, où on le consulte vraiment, et la recherche
       * continue de le fouiller : taper une adresse retrouve son client.
       */
      key: 'phone',
      label: 'Téléphone',
      sortKey: 'phone',
      render: (c) =>
        c.phone ? (
          <Typography variant="body2" className="tabular">
            {c.phone}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
    { key: 'city', label: 'Ville', sortKey: 'city', render: (c) => c.city ?? '—' },
    {
      /**
       * ⚠️ « Créé le » et « Dernière activité » ne disent PAS la même chose,
       * et la confusion est facile : la première est l'ancienneté du client,
       * la seconde la fraîcheur de la relation. Un client de 2019 qui n'a rien
       * acheté depuis deux ans se repère en comparant les deux — d'où leur
       * voisinage dans le tableau.
       */
      key: 'createdAt',
      label: 'Créé le',
      sortKey: 'createdAt',
      render: (c) => new Date(c.createdAt).toLocaleDateString('fr-FR'),
    },
    {
      key: 'activity',
      label: 'Dernière activité',
      sortKey: 'lastActivityAt',
      render: (c) =>
        c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString('fr-FR') : '—',
    },
    {
      /**
       * ⭐ Ce que le client a acheté — la question qu'on pose vraiment à un
       * fichier clients. Trier dessus fait remonter les meilleurs clients en un
       * clic, ce qu'aucun autre écran ne permettait.
       *
       * ⚠️ Cumul TTC des ventes NON ANNULÉES, brouillons et devis exclus :
       * compter un devis ferait passer pour bon client quelqu'un qui n'a encore
       * rien payé.
       */
      key: 'purchases',
      label: 'Montant des achats',
      sortKey: 'totalPurchases',
      align: 'right',
      numeric: true,
      render: (c) =>
        c.totalPurchases && c.totalPurchases > 0 ? (
          <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
            {prix(c.totalPurchases)}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
    {
      /**
       * ⭐ Le SOLDE du client, signé et coloré dans les deux sens.
       *
       * Positif, il doit encore à la boutique. Négatif, c'est la boutique qui
       * lui doit — le cas du comptoir sans monnaie, où l'on encaisse le billet
       * entier en restant devoir la différence. Ce second sens n'apparaissait
       * nulle part et se réglait de mémoire.
       *
       * ⚠️ Cumulé sur ses documents FACTURÉS : devis et commandes sont des
       * engagements, rien n'y est dû tant qu'ils ne sont pas transformés.
       */
      key: 'solde',
      label: 'Reste dû',
      align: 'right',
      numeric: true,
      render: (c) => {
        const solde = c.solde ?? 0
        if (solde === 0) {
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              —
            </Typography>
          )
        }
        const boutiqueDoit = solde < 0
        return (
          <Tooltip
            title={
              boutiqueDoit
                ? 'La boutique doit cette somme au client'
                : 'Le client doit cette somme à la boutique'
            }
          >
            <Typography
              variant="body2"
              className="tabular"
              sx={{ fontWeight: 600, color: boutiqueDoit ? 'error.main' : 'warning.main' }}
            >
              {boutiqueDoit ? '− ' : '+ '}
              {prix(Math.abs(solde))}
            </Typography>
          </Tooltip>
        )
      },
    },
    {
      /**
       * Étiquettes libres — « Fidèle », « Grossiste », « Mauvais payeur ».
       *
       * ⚠️ La colonne restait DÉSESPÉRÉMENT vide : le formulaire de saisie
       * envoyait `tags: []` en dur, aucun écran ne permettait donc d'en poser
       * une. La colonne existait, la fonction non.
       */
      key: 'tags',
      label: 'Étiquettes',
      sortKey: 'tags',
      render: (c) =>
        c.tags.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {c.tags.slice(0, 3).map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                variant="outlined"
                // Cliquer une étiquette filtre la liste : c'est le geste qu'on
                // tente naturellement, autant qu'il fasse quelque chose.
                onClick={(e) => {
                  e.stopPropagation()
                  patch({ tag: t })
                }}
              />
            ))}
            {c.tags.length > 3 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                +{c.tags.length - 3}
              </Typography>
            )}
          </Box>
        ),
    },
  ]

  const activeFilters = [query.city, query.type, query.tag].filter(Boolean).length

  return (
    <Box>
      {message && (
        <Alert severity="warning" sx={{ mb: 4 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Box>
          <Typography variant="h1">Clients</Typography>
          {page && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {page.total} client{page.total > 1 ? 's' : ''}
              {activeFilters > 0 && ` · ${activeFilters} filtre${activeFilters > 1 ? 's' : ''}`}
            </Typography>
          )}
        </Box>
        {/* `flexWrap` : sur un téléphone, quatre actions ne tiennent pas sur
            une ligne — sans lui, la dernière sortait de l'écran. */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
            Importer
          </Button>
          {/* ⭐ Toujours visible, y compris sur ordinateur : c'est là qu'on
              prépare le fichier qu'on s'enverra ensuite sur le téléphone. */}
          <Button startIcon={<ContactPhoneIcon />} onClick={() => setContactsOpen(true)}>
            Ajouter au téléphone
          </Button>
          {/* ⭐ N'apparaît QUE sur sélection, comme la suppression : un export
              permanent invite à exporter tout le fichier clients par mégarde,
              et l'utilisateur qui coche trois lignes veut ces trois lignes. */}
          {selection.length > 0 && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (!store) return
                const search = new URLSearchParams()
                for (const [k, v] of Object.entries(query)) {
                  if (v !== undefined && k !== 'limit' && k !== 'offset') search.set(k, String(v))
                }
                search.set('ids', selection.join(','))
                void api
                  .telecharger(
                    `/stores/${store.storeId}/customers/export?${search.toString()}`,
                    `clients-${new Date().toISOString().slice(0, 10)}.csv`,
                  )
                  /* ⚠️ Ne JAMAIS avaler l'erreur ici. Hors ligne, l'export
                     échouait sans un mot : on cliquait, rien ne se passait,
                     et rien ne disait pourquoi. */
                  .catch((e: unknown) =>
                    setMessage(e instanceof Error ? e.message : 'Export impossible.'),
                  )
              }}
            >
              Exporter ({selection.length})
            </Button>
          )}
          {/* N'apparaît QUE si quelque chose est coché : un bouton de
              suppression toujours présent invite à l'accident. */}
          {selection.length > 0 && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmation(true)}
            >
              Supprimer ({selection.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setFormOpen(true)}
          >
            Nouveau client
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Nom, téléphone, e-mail, ville…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Ville"
            size="small"
            value={query.city ?? ''}
            onChange={(e) => patch({ city: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Toutes</MenuItem>
            {cities.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            size="small"
            value={query.type ?? ''}
            onChange={(e) => patch({ type: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="individual">Particulier</MenuItem>
            <MenuItem value="company">Société</MenuItem>
          </TextField>
          {/* N'apparaît que si des étiquettes EXISTENT : un filtre à une seule
              entrée « Toutes » n'apprend rien et encombre la barre. */}
          {etiquettes.length > 0 && (
            <TextField
              select
              label="Étiquette"
              size="small"
              value={query.tag ?? ''}
              onChange={(e) => patch({ tag: e.target.value })}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Toutes</MenuItem>
              {etiquettes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          )}
          {(query.q || activeFilters > 0) && (
            <Button onClick={() => { setDraft(''); setParams({}, { replace: true }) }}>
              Réinitialiser
            </Button>
          )}
        </Box>
      </Paper>

      <DataTable
        columns={columns}
        rows={page?.data ?? []}
        rowKey={(c) => c.id}
        loading={loading}
        total={page?.total ?? 0}
        limit={query.limit ?? 50}
        offset={query.offset ?? 0}
        sort={query.sort}
        onSortChange={(sort) => patch({ sort })}
        onPageChange={(offset, limit) =>
          patch({ offset: String(offset), limit: String(limit) })
        }
        onRowClick={(c) => navigate(`/s/${slug}/clients/${c.id}`)}
        selected={selection}
        onSelectionChange={setSelection}
        emptyState={
          <EmptyState
            icon={<PeopleIcon sx={{ fontSize: 48 }} />}
            title={query.q || activeFilters ? 'Aucun résultat' : 'Aucun client pour l’instant'}
            description={
              query.q || activeFilters
                ? 'Modifiez vos critères ou réinitialisez les filtres.'
                : 'Créez votre premier client ou importez votre fichier existant.'
            }
            action={
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setFormOpen(true)}
                >
                  Nouveau client
                </Button>
                <Button startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
                  Importer un fichier
                </Button>
              </Box>
            }
          />
        }
      />

      {store && (
        <>
          <CustomerFormDialog
            open={formOpen}
            storeId={store.storeId}
            initialName={prefillName}
            onClose={() => {
              setFormOpen(false)
              // Retire ?nouveau= de l'URL, sinon la boîte se rouvrirait
              // au moindre changement de filtre.
              if (prefillName) patch({ nouveau: undefined })
            }}
            onSaved={(customer) => navigate(`/s/${slug}/clients/${customer.id}`)}
          />

          <ConfirmDialog
            open={confirmation}
            title={`Supprimer ${selection.length} client${selection.length > 1 ? 's' : ''} ?`}
            message={
              `${selection.length > 1 ? 'Ces fiches n’apparaîtront plus' : 'Cette fiche n’apparaîtra plus'} ` +
              'dans les listes ni dans la recherche. Les documents déjà émis sont conservés.'
            }
            onConfirm={async () => {
              // Une par une : la suppression passe par la file d'attente, et un
              // échec sur l'une ne doit pas empêcher les autres.
              for (const id of selection) {
                const fiche = page?.data.find((c) => c.id === id)
                if (fiche) await removeCustomer(store.storeId, fiche)
              }
              setSelection([])
            }}
            onClose={() => setConfirmation(false)}
          />

          <ContactsTelephoneDialog
            open={contactsOpen}
            storeId={store.storeId}
            nomBoutique={store.name}
            pays={store.countryCode ?? null}
            clients={toutes?.data ?? []}
            selection={selection}
            onClose={() => setContactsOpen(false)}
          />

          <ImportDialog
            open={importOpen}
            storeId={store.storeId}
            onClose={() => setImportOpen(false)}
            onImported={() => {
              // ⚠️ L'import écrit sur le SERVEUR ; la liste, elle, lit la
              // réplique locale. Sans ce cycle immédiat, les lignes importées
              // restent invisibles jusqu'à la prochaine synchronisation — et
              // l'utilisateur, croyant à un échec, relance l'import.
              if (store) void runCycle(store.storeId)
            }}
          />
        </>
      )}
    </Box>
  )
}
